import React, { useCallback, useRef, useState } from 'react';
import {
  ReactFlow,
  Background,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
  BackgroundVariant,
  type Connection,
  type Node,
  type Edge,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { TriggerNode } from './nodes/TriggerNode';
import { ActionNode } from './nodes/ActionNode';
import { ConditionNode } from './nodes/ConditionNode';
import { ApprovalNode } from './nodes/ApprovalNode';
import { LoopNode } from './nodes/LoopNode';
import { ForEachNode } from './nodes/ForEachNode';
import { WorkflowEdge } from './WorkflowEdge';
import { computeZoomRange } from './canvasZoom';
import { isMobileViewport } from '../../hooks/useMobile';
import type { WorkflowDefinition } from '../../api/client';

const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
  approval: ApprovalNode,
  refine_approval: ApprovalNode,
  loop: LoopNode,
  for_each: ForEachNode,
};

const edgeTypes = {
  workflow: WorkflowEdge,
};

const DEFAULT_MARKER_END = { type: MarkerType.ArrowClosed as MarkerType, width: 16, height: 16 };

function toFlowNodes(definition: WorkflowDefinition, selectedNodeId: string | null): Node[] {
  return definition.nodes.map(n => ({
    id: n.id,
    type: n.type,
    data: n.data,
    position: n.position,
    selected: n.id === selectedNodeId,
  } as Node));
}

function toFlowEdges(definition: WorkflowDefinition): Edge[] {
  return definition.edges.map((e, i) => {
    let sourceHandle: string | undefined;
    if (e.label === 'approved' || e.label === 'rejected') {
      sourceHandle = e.label;
    } else if (e.label === 'true' || e.label === 'false') {
      sourceHandle = e.label;
    } else if (e.label === 'met' || e.label === 'timeout') {
      sourceHandle = e.label;
    }
    return {
      id: e.id || `auto-e${i + 1}`,
      source: e.source,
      target: e.target,
      sourceHandle,
      label: e.label,
      type: 'workflow',
      markerEnd: DEFAULT_MARKER_END,
      animated: true,
    } as Edge;
  });
}

interface WorkflowCanvasProps {
  definition: WorkflowDefinition;
  onChange: (definition: WorkflowDefinition) => void;
  onNodeSelect: (nodeId: string | null) => void;
  selectedNodeId: string | null;
}

let nodeIdCounter = 0;

function getNextId(prefix: string) {
  nodeIdCounter += 1;
  return `${prefix}-${nodeIdCounter}`;
}

export function WorkflowCanvas({
  definition,
  onChange,
  onNodeSelect,
  selectedNodeId,
}: WorkflowCanvasProps) {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const reactFlowInstance = useRef<ReactFlowInstance | null>(null);
  const [zoomRange, setZoomRange] = useState<{ min: number; max: number }>({ min: 0.1, max: 2 });
  const [nodes, setNodes, onNodesChange] = useNodesState(toFlowNodes(definition, selectedNodeId));
  const [edges, setEdges, onEdgesChange] = useEdgesState(toFlowEdges(definition));

  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const edgesRef = useRef(edges);
  edgesRef.current = edges;

  const syncDefinition = useCallback(
    (newNodes: Node[], newEdges: Edge[]) => {
      onChange({
        nodes: newNodes.map(n => ({
          id: n.id,
          type: n.type as any,
          data: n.data,
          position: n.position,
        })),
        edges: newEdges.map(e => ({
          id: e.id,
          source: e.source,
          target: e.target,
          label: (e.label as string) || undefined,
        })),
      });
    },
    [onChange],
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      let label: string | undefined;
      if (connection.sourceHandle === 'true' || connection.sourceHandle === 'false') {
        label = connection.sourceHandle;
      } else if (connection.sourceHandle === 'approved' || connection.sourceHandle === 'rejected') {
        label = connection.sourceHandle;
      } else if (connection.sourceHandle === 'met' || connection.sourceHandle === 'timeout') {
        label = connection.sourceHandle;
      }
      const newEdge: Edge = {
        id: getNextId('e'),
        source: connection.source || '',
        target: connection.target || '',
        type: 'workflow',
        markerEnd: DEFAULT_MARKER_END,
        animated: true,
        ...(label ? { label, data: { label } } : {}),
      };
      setEdges(eds => {
        const updated = addEdge(newEdge, eds) as Edge[];
        syncDefinition(nodesRef.current, updated);
        return updated;
      });
    },
    [setEdges, syncDefinition],
  );

  const onNodeDragStop = useCallback(
    (_: any, __: any) => {
      syncDefinition(nodesRef.current, edgesRef.current);
    },
    [syncDefinition],
  );

  const handleSelectionChange = useCallback(
    ({ nodes: selectedNodes }: { nodes: Node[] }) => {
      if (selectedNodes.length === 1) {
        onNodeSelect(selectedNodes[0].id);
      } else if (selectedNodes.length === 0) {
        onNodeSelect(null);
      }
    },
    [onNodeSelect],
  );

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      onNodeSelect(node.id);
      setNodes(nds => nds.map(n => ({ ...n, selected: n.id === node.id })));
    },
    [onNodeSelect, setNodes],
  );

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const nodeType = event.dataTransfer.getData('application/reactflow-type');
      const dataStr = event.dataTransfer.getData('application/reactflow-data');
      if (!nodeType || !dataStr) return;

      const data = JSON.parse(dataStr);
      const wrapper = reactFlowWrapper.current;
      if (!wrapper) return;

      const position = reactFlowInstance.current
        ? reactFlowInstance.current.screenToFlowPosition({ x: event.clientX, y: event.clientY })
        : { x: event.clientX - wrapper.getBoundingClientRect().left, y: event.clientY - wrapper.getBoundingClientRect().top };

      const newNode: Node = {
        id: getNextId(nodeType),
        type: nodeType,
        position,
        data,
      };

      setNodes(nds => {
        const updated: Node[] = [...nds, newNode];
        syncDefinition(updated, edgesRef.current);
        return updated;
      });
    },
    [setNodes, syncDefinition],
  );

  const initialDefinitionRef = useRef(true);

  React.useEffect(() => {
    nodeIdCounter = Math.max(
      nodeIdCounter,
      ...definition.nodes.map(n => {
        const m = n.id?.match(/-(\d+)$/);
        return m ? parseInt(m[1], 10) : 0;
      }),
      ...definition.edges.map(e => {
        const m = e.id?.match(/(\d+)$/);
        return m ? parseInt(m[1], 10) : 0;
      }),
    );
    if (initialDefinitionRef.current) {
      setNodes(toFlowNodes(definition, selectedNodeId));
      setEdges(toFlowEdges(definition));
      initialDefinitionRef.current = false;
    } else {
      const currentNodeCount = nodes.length;
      const newNodeCount = definition.nodes.length;
      if (currentNodeCount !== newNodeCount) {
        // Node count changed (NL regeneration, add/remove) — full reset
        setNodes(toFlowNodes(definition, selectedNodeId));
        setEdges(toFlowEdges(definition));
      } else {
        // Node count unchanged — sync data from definition into existing nodes
        // while preserving canvas-managed position and selection state.
        const defDataMap = new Map(definition.nodes.map(n => [n.id, n.data]));
        setNodes(nds =>
          nds.map(n => {
            const newData = defDataMap.get(n.id);
            if (newData && newData !== n.data) {
              return { ...n, data: newData };
            }
            return n;
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definition]);

  return (
    <div ref={reactFlowWrapper} className="flex-1 h-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeDragStop={onNodeDragStop}
        onSelectionChange={handleSelectionChange}
        onNodeClick={handleNodeClick}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onInit={(instance) => {
          reactFlowInstance.current = instance;
          // The declarative fitView prop can race node measurement on busy
          // first paints (e.g. hydrating a shared/generated workflow), which
          // used to lock the zoom range at the unfitted default and leave
          // wide workflows cut off. Wait until every node reports measured
          // dimensions, fit explicitly, then lock the zoom range around the
          // actually-fitted zoom.
          let attempts = 0;
          const fitWhenMeasured = () => {
            attempts += 1;
            const flowNodes = instance.getNodes();
            const measured = flowNodes.every(n => ((n.measured?.width ?? 0) > 0));
            if ((measured && flowNodes.length > 0) || attempts > 15) {
              Promise.resolve(instance.fitView({ padding: 0.3, maxZoom: 1.1 })).then(() => {
                const zoom = instance.getViewport().zoom;
                setZoomRange(computeZoomRange(zoom, isMobileViewport()));
              });
              return;
            }
            setTimeout(fitWhenMeasured, 100);
          };
          setTimeout(fitWhenMeasured, 100);
        }}
        defaultEdgeOptions={{
          type: 'workflow',
          markerEnd: DEFAULT_MARKER_END,
          animated: true,
        }}
        fitView
        fitViewOptions={{ padding: 0.3, maxZoom: 1.1 }}
        minZoom={zoomRange.min}
        maxZoom={zoomRange.max}
        zoomOnScroll={true}
        zoomOnPinch={true}
        zoomOnDoubleClick={false}
        proOptions={{ hideAttribution: true }}
        style={{ background: 'transparent' }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
      </ReactFlow>
    </div>
  );
}
